import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { ToastContainer, toast } from "react-toastify";
import {
  getAllHighLevelSessions,
  getHighLevelSessionsByAdherent,
  deleteHighLevelSession,
  toAbsoluteAdherentPhotoUrl,
} from "../../../services/highLevelSessionServices";
import { getAllAdherentsList } from "../../../services/adherentServices";
import { FaFilter, FaPlus, FaSearch, FaUserCircle, FaInfoCircle } from "react-icons/fa";

import AddHighLevelSessionModal from "./AddHighLevelSessionModal";
import EditHighLevelSessionModal from "./EditHighLevelSessionModal";

/* =========================
   Helpers / constantes
   ========================= */
const CATEGORIES = ["Entrainement Judo club", "Entrainement Structure", "Technique club", "Technique structure", "Musculation", "Course ", "Prépa associé ou dissocié", "Stage ou Training camp", "Competition"];
const fmt = (d, f = "DD/MM/YY") => (moment(d).isValid() ? moment(d).format(f) : "—");

/* =========================
   Page principale
   ========================= */
export default function HighLevelSessionsPage() {
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // Données
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filtres
  const [search, setSearch] = useState(""); // nom/prénom/nom_seance
  const [adherents, setAdherents] = useState([]);
  const [adherentId, setAdherentId] = useState("");
  const [categorie, setCategorie] = useState("");
  const [isDone, setIsDone] = useState(""); // "", "true", "false"
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Modales
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  /* ===== Fetch listes adhérents ===== */
  const loadAdherents = useCallback(async () => {
    try {
      const list = await getAllAdherentsList();
      const arr = Array.isArray(list) ? list.slice() : [];
      arr.sort((a, b) =>
        `${a?.nom || ""} ${a?.prenom || ""}`.localeCompare(
          `${b?.nom || ""} ${b?.prenom || ""}`,
          "fr",
          { sensitivity: "base" }
        )
      );
      setAdherents(arr);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des adhérents");
    }
  }, []);

  useEffect(() => {
    loadAdherents();
  }, [loadAdherents]);

  /* ===== Fetch séances ===== */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        categorie_seance: categorie || undefined,
        is_done: isDone !== "" ? isDone : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        limit: 500,
      };

      const data = adherentId
        ? await getHighLevelSessionsByAdherent(adherentId, filters)
        : await getAllHighLevelSessions(filters);

      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors du chargement des séances");
    } finally {
      setLoading(false);
    }
  }, [adherentId, categorie, isDone, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const onRefresh = () => setRefreshKey((k) => k + 1);

  /* ===== Recherche texte locale ===== */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const fullName = `${it?.adherent?.nom || ""} ${it?.adherent?.prenom || ""}`;
      const nomSeance = it?.nom_seance || "";
      const cat = it?.categorie_seance || "";
      return (
        fullName.toLowerCase().includes(q) ||
        nomSeance.toLowerCase().includes(q) ||
        cat.toLowerCase().includes(q)
      );
    });
  }, [items, search]);

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle role="Coach" title="Suivi Haut Niveau — Séances" align="center" className="mb-1" />
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau header */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 text-white">
                <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                  <FaInfoCircle />
                </div>
                <div>
                  <p className="text-lg sm:text-xl font-semibold">Instructions</p>
                  <ul className="text-sm text-white/90 leading-relaxed list-disc pl-5">
                    <li>Filtrez par adhérent, catégorie, statut et période.</li>
                    <li>Ajoutez, modifiez, supprimez les séances de vos athlètes.</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button
                  text="Nouvelle séance"
                  icon={FaPlus}
                  onClick={() => setShowAdd(true)}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-1"
                />
              </div>
            </div>
          </div>

          {/* Filtres */}
          <div className="px-5 pt-5 sm:px-8 pb-3">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              {/* Recherche texte */}
              <div className="md:col-span-2">
                <label htmlFor="search" className="sr-only">
                  Recherche
                </label>
                <div className="relative">
                  <input
                    id="search"
                    type="text"
                    placeholder="Rechercher (adhérent, catégorie, nom de séance)…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              {/* Adhérent */}
              <div>
                <select
                  value={adherentId}
                  onChange={(e) => setAdherentId(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                >
                  <option value="">Tous les adhérents</option>
                  {adherents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nom} {a.prenom}
                    </option>
                  ))}
                </select>
              </div>

              {/* Catégorie */}
              <div>
                <select
                  value={categorie}
                  onChange={(e) => setCategorie(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                >
                  <option value="">Toutes catégories</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* Statut */}
              <div>
                <select
                  value={isDone}
                  onChange={(e) => setIsDone(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                >
                  <option value="">Tous statuts</option>
                  <option value="true">Terminé</option>
                  <option value="false">Planifié</option>
                </select>
              </div>

              {/* Dates */}
              <div>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
                />
              </div>
            </div>

            {/* Actions filtres */}
            <div className="mt-3 flex items-center gap-2">
              <Button text="Appliquer filtres" icon={FaFilter} onClick={fetchData} className="!rounded-xl" />
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setCategorie("");
                  setIsDone("");
                  setDateFrom("");
                  setDateTo("");
                  setAdherentId("");
                  setRefreshKey((k) => k + 1);
                }}
                className="inline-flex items-center px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 text-sm"
              >
                Réinitialiser
              </button>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Loading */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-20"
                  />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">Aucune séance</h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajustez vos filtres ou ajoutez une nouvelle séance.
                </p>
              </div>
            )}

            {/* Table Desktop */}
            {!loading && filtered.length > 0 && (
              <div className="hidden md:block">
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                          {[
                            "Adhérent",
                            "Catégorie",
                            "Séance",
                            "Lieu",
                            "Date",
                            "Durée",
                            "Qualité",
                            "Statut",
                            "Actions",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                        {filtered.map((it) => {
                          const avatar =
                            it?.adherent?.photo_url_abs ||
                            toAbsoluteAdherentPhotoUrl(it?.adherent?.photo_url);
                          const name = `${it?.adherent?.nom || ""} ${it?.adherent?.prenom || ""}`.trim();

                          return (
                            <tr
                              key={it.id}
                              className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3 min-w-0">
                                  {avatar ? (
                                    <img
                                      src={avatar}
                                      alt={name || "Adhérent"}
                                      className="h-9 w-9 rounded-full object-cover ring-2 ring-slate-200 dark:ring-slate-700"
                                    />
                                  ) : (
                                    <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-500">
                                      <FaUserCircle />
                                    </div>
                                  )}
                                  <div className="truncate">
                                    <div className="font-medium text-slate-800 dark:text-slate-100 truncate">
                                      {name || "—"}
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                      {it?.adherent?.email || ""}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {it?.categorie_seance || "—"}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {it?.nom_seance || "—"}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {it?.lieu_seance || "—"}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {fmt(it?.seance_date)}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {it?.duree_minutes ?? 0} min
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                {it?.qualite_note ?? "—"}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${
                                    it?.is_done
                                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                                      : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                  }`}
                                >
                                  {it?.is_done ? "Terminé" : "Planifié"}
                                </span>
                              </td>

                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <ButtonEdit onClick={() => setEditing(it)} />
                                  <ButtonDelete
                                    onClick={async () => {
                                      if (!window.confirm("Supprimer cette séance ?")) return;
                                      try {
                                        await deleteHighLevelSession(it.id);
                                        toast.success("Séance supprimée");
                                        onRefresh();
                                      } catch (e) {
                                        console.error(e);
                                        toast.error("Erreur lors de la suppression");
                                      }
                                    }}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Cartes Mobile */}
            {!loading && filtered.length > 0 && (
              <div className="md:hidden space-y-3">
                {filtered.map((it) => {
                  const avatar =
                    it?.adherent?.photo_url_abs ||
                    toAbsoluteAdherentPhotoUrl(it?.adherent?.photo_url);
                  const name = `${it?.adherent?.prenom || ""} ${it?.adherent?.nom || ""}`.trim();
                  return (
                    <div
                      key={`card-${it.id}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {avatar ? (
                            <img
                              src={avatar}
                              alt={name || "Adhérent"}
                              className="h-10 w-10 rounded-full object-cover ring-2 ring-slate-200 dark:ring-slate-700"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-500">
                              <FaUserCircle />
                            </div>
                          )}
                          <div className="truncate">
                            <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                              {name || "—"}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                              {it?.adherent?.email || ""}
                            </div>
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            it?.is_done
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                              : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                          }`}
                        >
                          {it?.is_done ? "Terminé" : "Planifié"}
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">Catégorie</div>
                          <div className="font-medium text-slate-800 dark:text-slate-100">
                            {it?.categorie_seance || "—"}
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2">
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">Date</div>
                          <div className="font-medium text-slate-800 dark:text-slate-100">
                            {fmt(it?.seance_date)}
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/40 p-2 col-span-2">
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">Séance</div>
                          <div className="font-medium text-slate-800 dark:text-slate-100">
                            {it?.nom_seance || "—"}
                          </div>
                        </div>
                      </div>

                      {!!it?.observations_seance && (
                        <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                          {it.observations_seance}
                        </p>
                      )}

                      <div className="mt-3 flex items-center gap-2">
                        <ButtonEdit onClick={() => setEditing(it)} />
                        <ButtonDelete
                          onClick={async () => {
                            if (!window.confirm("Supprimer cette séance ?")) return;
                            try {
                              await deleteHighLevelSession(it.id);
                              toast.success("Séance supprimée");
                              onRefresh();
                            } catch (e) {
                              console.error(e);
                              toast.error("Erreur lors de la suppression");
                            }
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      {showAdd && (
        <AddHighLevelSessionModal
          isOpen={showAdd}
          onClose={() => setShowAdd(false)}
          afterSave={() => {
            setShowAdd(false);
            onRefresh();
          }}
          adherents={adherents}
          categories={CATEGORIES}
        />
      )}

      {editing && (
        <EditHighLevelSessionModal
          isOpen={!!editing}
          onClose={() => setEditing(null)}
          afterSave={() => {
            setEditing(null);
            onRefresh();
          }}
          session={editing}
          adherents={adherents}
          categories={CATEGORIES}
        />
      )}
    </section>
  );
}
